import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { fieldId, SelectField, TextField, useForm, type FormErrors } from '../../auth/CompanyFields';
import { t } from '../../i18n';
import { eurC } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { needsNote, noteMissing, shortArt } from './labels';

// „Unterlagen anfordern“: Unterlage wählen, Zeitraum oder Hinweis dazu. Für die Firma (Übersicht) und für einen
// Mitarbeiter (Personalakte); url ist die Adresse, an die die Anfrage geht. placeholder und hint kommen übersetzt.
// Kostet die Unterlage etwas, steht der Preis unter der Auswahl (Gesetzes-Prüfung B18); genau der wird berechnet.
export function RequestForm({
  arts,
  url,
  placeholder,
  hint,
  onDone,
  onCancel,
}: {
  arts: string[];
  url: string;
  placeholder: string;
  hint: string;
  onDone(): void;
  onCancel(): void;
}) {
  const toast = useToast();
  const f = useForm({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const art = f.values.art ?? '';
  const need = needsNote(art);
  const prices = useApi<{ prices: Record<string, number>; vatRate: number }>('/api/app/anfragen-preise').data;
  const price = prices?.prices[art];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const note = (f.values.note ?? '').trim();
    const errors: FormErrors = {};
    if (!art) errors.art = t('Bitte auswählen, was Sie brauchen.');
    else if (need && !note) errors.note = t(noteMissing(art));
    const first = (['art', 'note'] as const).find((k) => errors[k]);
    if (first) {
      f.setErrors(errors);
      document.getElementById(fieldId(first))?.focus();
      toast(errors[first]!);
      return;
    }
    setBusy(true);
    try {
      await api.post(url, { art, note });
      toast(t('{art} angefordert', { art: t(shortArt(art)) }));
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <form className="form maform rqform" onSubmit={submit} noValidate>
      <SelectField f={f} name="art" label={t('Was brauchen Sie?')} required options={arts} placeholder={t('Bitte wählen')} />
      {price !== undefined && (
        <p className="rq-price" role="status">
          {prices!.vatRate
            ? t('Kostet {price} zzgl. USt. und kommt auf Ihre nächste Rechnung.', { price: eurC(price) })
            : t('Kostet {price} und kommt auf Ihre nächste Rechnung.', { price: eurC(price) })}
        </p>
      )}
      <TextField
        f={f}
        name="note"
        label={need ? t('Zeitraum oder Hinweis') : t('Zeitraum oder Hinweis (optional)')}
        required={need}
        maxLength={300}
        placeholder={placeholder}
        autoComplete="off"
      />
      {error && (
        <p className="formerr" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : t('Anfordern')}
        </button>
        <button className="btn2" type="button" onClick={onCancel}>
          {t('Abbrechen')}
        </button>
      </div>
      <div className="src">{hint}</div>
    </form>
  );
}
